import { App, SignInState } from "../app";
import { SearchResult, SearchResults } from "../SearchResults";
import { CropRect } from "../CropRect";
import { TagsResult } from "../TagsResult";
import { ResourceStrings } from '../ResourceStrings';
import { CommentType, FeedbackTag } from '../PublicFeedback';
import { clientVersion } from '../telemetry/TelemetryHelper';
import { Settings } from '../Settings';
import { platformActions } from "../communication/Communication";
import { SettingsUpdate } from "../SettingsUpdate";
import { EntryValue } from "../constants/urlParams";
import { generateTrackingId, isNullOrEmpty, stringFromErrorOrAny } from "../utils/tools";
import { getVarsFromUrl } from "../utils/webPage";
import { getFileName } from "../utils/files";
import { AccountType, LocalServerActions, NativeActions, TrackingIdInfo, UserInfo } from "../communication/PlatformActions";
import { Operation, UIOperationState } from "./operation";
import { SpaceId } from "../config/spaces/spaceIds";
import { hasItems } from "../utils/collections";
import { ModerationListFilter } from "../Moderation";
import { PartialPost, ReactionType, RootPost, PostType, PostId, RootPostId } from "../RootPost";
import {
    AppState,
    Dialog,
    FileInfo,
    Filter,
    FollowTab,
    MainView,
    RapView,
    SearchInfoTarget,
    Sort,
    VSInstanceInfo,
    TypesFilterFlags,
    SearchTab,
    SearchInfo,
    ActionRecorderState,
    EntryValuePostType,
    NotificationInfo,
    AttachmentPostInfo,
    StateGroupFilter,
    RapSource,
    UserServerSettings,
    OperationState,
} from "./appState";
import { instance } from "../config/serverConfig";
import { processSignInPromise } from "../communication/PlatformHelpers";
import { getDevComUrl } from "../constants/urls";
import { root } from "postcss";

export enum Apis {
    SearchQuerySearchV2 = '/searchquery/searchV2',
    SearchQuerySearchComplete = '/searchquery/searchComplete',
    SearchQueryItem = '/searchquery/item',

    DetailsV2SubmitComment = '/detailsV2/submitComment',
    DetailsV2SubmitSolution = '/detailsV2/submitSolution',
    DetailsV2IsPublicBuild = '/detailsV2/isPublicBuild',
    DetailsV2GetReleaseAction = '/detailsV2/getReleaseActionInfo',
    DetailsV3RootPost = '/detailsV3/rootPost',
    DetailsV3Summary = '/detailsV3/summary',
    DetailsV3FeatureFeedback = '/detailsV3/featureFeedback',

    V2Verbatim = "/V2Verbatim",
    V2VerbatimCheckAuth = "/V2Verbatim/checkAuth",

    NodeReact = '/node/react',
    NodeFlag = '/node/flag',
    NodeVote = '/node/vote',
    NodeReactivate = '/node/reactivate',
    NodeConvert = '/node/convert',
    NodeUpdate = '/node/update',
    NodeDelete = '/node/delete',

    TagsGet = "/tags",

    // Moderation
    ModerationGetFlaggedPosts = '/moderation/getFlaggedPosts',
    ModerationPublishPost = '/moderation/publishPost',
    ModerationRejectPost = '/moderation/rejectPost',
    ModerationRecentlyCreatedPosts = '/moderation/getRecentlyCreatedPosts',
    ModerationAutoRejectedPosts = '/moderation/getAutoRejectedPosts',

    // Uploads
    BlobUploadRequest = "/fileBlob/blobUploadRequest",
    BlobUploadRequestByAHId = "/fileBlob/blobUploadRequestByAHId",

    FileBlobGetSecureAccessToken = "/fileBlob/getSecureAccessToken",

    UserPropertiesGetUserProperties = "/userProperties/getUserProperties",
    UserPropertiesGetActivity = "/userProperties/getActivity",
    UserPropertiesFollow = "/userProperties/follow",
    UserPropertiesUnfollow = "/userProperties/unfollow",
    UserPropertiesIsFollowing = "/userProperties/isFollowing",
    UserPropertiesList = "/userProperties/list",
    UserPropertiesMetadata = "/userProperties/metadata",
    UserPropertiesProfile = "/userProperties/profile",
    UserPropertiesSettings = "/userProperties/settings",

    UserPropertiesUpdateSuspensionState = '/userProperties/updateSuspensionState',
    UserPropertiesSearchUsers = '/userProperties/searchUsers',

    AnnouncementIncidents = "/announcement/incidents",
    AnnouncementAnnouncements = "/announcement/announcements",
    AnnouncementStatus = "/announcement/status",
    AnnouncementCustomOutage = "/announcement/customStatus",

    DiagnosticsApprovedFileExtensions = "/diagnostics/approvedFileExtensions",
    DiagnosticsApproveFilesToUpload = "/diagnostics/approveFilesToUpload",
    DiagnosticsAttachmentsPerUser = "/diagnostics/attachmentsPerUser",
    DiagnosticsDeleteAttachment = "/diagnostics/deleteAttachment",

    ExportRestrictExport = "/exportRestrict/export",
    ExportRestrictDelete = "/exportRestrict/delete",
    ExportRestrictStatus = "/exportRestrict/status",
}

/** APIs associated with the current devcom server instance 
 (e.g. developercommunity.visualstudio.com rather than sendvsfeedback2.azurewebsites.net) */
enum LocalServerApis {
    Config = "/config",
    ConfigAuditLogs = "/config/auditLogs",
    ConfigFeatureFlags = "/config/featureFlags",
    ConfigModAdminUsers = "/config/modAdminUsers",
    ConfigRouting = "/config/routing",
    ConfigSpace = "/config/space",
    ConfigWebsite = "/config/website",
    ConfigPipelineStatus = "/config/pipelineStatus",
    ConfigGdprStatus = "/config/gdprStatus",

    ReadOnlyDevCom = "/readonly/devcom",
}

export const ACTIVITY_PAGE_SIZE: number = 20;

export const requestAction = (baseType: string): string => `${baseType}_REQUEST`;
export const successAction = (baseType: string): string => `${baseType}_SUCCESS`;
export const failureAction = (baseType: string): string => `${baseType}_FAILURE`;

export enum VoteType {
    voteUp = "voteup",
    voteDown = "votedown",
    cancelVote = "cancelvote",
}

export enum NodeVisibility {
    all = "all",
    msonly = "msonly",
    privateToCustomer = "privatetocustomer",
}

export class AddCommentRequest {
    item: PartialPost;
    commentText: string;
    parentId: number;
    visibility: NodeVisibility;
    isProvideInfo?: boolean = false;
    newNodeTrackingId?: string;
    operationKey?: string;
    includeNativeAttachments?: boolean = false;
}

export enum SuspensionActionType {
    Suspend = 1,
    Unsuspend = 2,
}

/** Sentiment about a feature. Used in the "was this helpful" control */
export enum FeatureSentiment {
    /** Sent when user commented for a "like" sentiment */
    CommentForLike = 0,

    /** User liked the feature */
    Like = 1,

    /** User disliked the feature */
    Dislike = 2,

    /** User required AI to regenerate a prompt (to be treated similar to "No") */
    Regenerate = 3,

    /** Sent when user commented for a "dislike" sentiment */
    CommentForDislike = 4,
}

export type FeatureName = string;
export class FeatureNames {
    public static readonly UseCategories: FeatureName = "UseCategories";
    public static readonly AISummary: FeatureName = "AISummary";
}

export class Actions {
    public static lastSearchId: number = 0;
    public static lastSuggestId: number = 0;
    public static lastSelectedProblemId: number = 0;
    public static lastGetActivityId: number = 0;

    public static SET_SITE_READONLY_SETTING: string = "SET_SITE_READONLY_SETTING";
    public static SET_APP_SETTINGS: string = "SET_APP_SETTINGS";

    public static SET_OPERATION: string = "SET_OPERATION";
    public static SET_OPERATION_STATE: string = "SET_OPERATION_STATE";

    public static DISCONNECTED_FROM_NATIVE_PROCESS: string = "DISCONNECTED_FROM_NATIVE_PROCESS";

    public static SET_GLOBAL_NOTIFICATION: string = "SET_GLOBAL_NOTIFICATION";
    public static SET_CONTEXTUAL_NOTIFICATION: string = "SET_CONTEXTUAL_NOTIFICATION";
    public static CLEAR_ALL_CONTEXTUAL_NOTIFICATION: string = "CLEAR_ALL_CONTEXTUAL_NOTIFICATION";

    // clears events sent to the UI for managing non-redux states
    // used when setting focus, etc
    public static CLEAR_EVENTS: string = 'CLEAR_EVENTS';

    // close button - track if it should show
    public static SET_CLOSEABLE: string = 'SET_CLOSEABLE';

    // true to show gudance dialog, false otherwise
    public static SET_SHOW_GUIDANCE: string = 'SET_SHOW_GUIDANCE';

    // sort and filter
    public static SET_SORT: string = 'SET_SORT';
    public static SET_FILTER: string = 'SET_FILTER';
    public static SET_TYPE_FILTER: string = 'SET_TYPE_FILTER';

    // search
    public static SEARCH: string = 'SEARCH';
    public static SEARCH_REQUEST: string = 'SEARCH_REQUEST';
    public static SEARCH_SUCCESS: string = 'SEARCH_SUCCESS';
    public static SEARCH_FAILURE: string = 'SEARCH_FAILURE';

    // vector (AI) search
    public static VECTOR_SEARCH: string = 'VECTOR_SEARCH';
    public static VECTOR_SEARCH_REQUEST: string = 'VECTOR_SEARCH_REQUEST';
    public static VECTOR_SEARCH_SUCCESS: string = 'VECTOR_SEARCH_SUCCESS';
    public static VECTOR_SEARCH_FAILURE: string = 'VECTOR_SEARCH_FAILURE';

    // suggestions for auto-complete
    public static SUGGEST: string = 'SUGGEST';
    public static SUGGEST_REQUEST: string = 'SUGGEST_REQUEST';
    public static SUGGEST_SUCCESS: string = 'SUGGEST_SUCCESS';
    public static SUGGEST_FAILURE: string = 'SUGGEST_FAILURE';

    // voting
    public static VOTE: string = 'VOTE';
    public static VOTE_REQUEST: string = 'VOTE_REQUEST';
    public static VOTE_SUCCESS: string = 'VOTE_SUCCESS';
    public static VOTE_FAILURE: string = 'VOTE_FAILURE';

    public static INIT_DETAILS_VIEW: string = 'INIT_DETAILS_VIEW';

    // search and vote
    public static LOAD_USER_PROPERTIES: string = 'LOAD_USER_PROPERTIES'; // load user properties (e.g. need more info IDs) for the logged in user
    public static LOAD_USER_PROPERTIES_REQUEST: string = 'LOAD_USER_PROPERTIES_REQUEST';
    public static LOAD_USER_PROPERTIES_SUCCESS: string = 'LOAD_USER_PROPERTIES_SUCCESS';
    public static LOAD_USER_PROPERTIES_FAILURE: string = 'LOAD_USER_PROPERTIES_FAILURE';

    public static GET_USER_METADATA: string = 'GET_USER_METADATA';

    public static SEARCH_USERS_FOR_ADMIN_VIEW: string = 'SEARCH_USERS_FOR_ADMIN_VIEW';    // Search users for admin page

    public static LOAD_TICKET_STATE_HISTORY: string = 'LOAD_TICKET_STATE_HISTORY';

    public static LOAD_FLAGGED_POSTS: string = 'LOAD_FLAGGED_POSTS';
    public static MODERATE_PUBLISH_POST: string = 'MODERATE_PUBLISH_POST';
    public static MODERATE_REJECT_POST: string = 'MODERATE_REJECT_POST';
    public static LOAD_RECENTLY_CREATED_POSTS: string = 'LOAD_RECENTLY_CREATED_POSTS';
    public static LOAD_AUTO_REJECTED_POSTS: string = 'LOAD_AUTO_REJECTED_POSTS';

    public static UPDATE_USER_SUSPENSION_STATE: string = 'UPDATE_USER_SUSPENSION_STATE';

    // "profile and preferences" view
    public static VIEW_ATTACHMENTS_REQUEST: string = 'VIEW_ATTACHMENTS_REQUEST';
    public static VIEW_ATTACHMENTS_SUCCESS: string = 'VIEW_ATTACHMENTS_SUCCESS';
    public static VIEW_ATTACHMENTS_FAILURE: string = 'VIEW_ATTACHMENTS_FAILURE';

    public static DELETE_ATTACHMENT: string = 'DELETE_ATTACHMENT';

    public static CREATE_ARCHIVE_REQUEST: string = 'CREATE_ARCHIVE_REQUEST';
    public static CREATE_ARCHIVE_SUCCESS: string = 'CREATE_ARCHIVE_SUCCESS';
    public static CREATE_ARCHIVE_FAILURE: string = 'CREATE_ARCHIVE_FAILURE';

    public static ARCHIVE_STATUS_REQUEST: string = 'ARCHIVE_STATUS_REQUEST';
    public static ARCHIVE_STATUS_SUCCESS: string = 'ARCHIVE_STATUS_SUCCESS';
    public static ARCHIVE_STATUS_FAILURE: string = 'ARCHIVE_STATUS_FAILURE';

    public static DELETE_ACCOUNT_REQUEST: string = 'DELETE_ACCOUNT_REQUEST';
    public static DELETE_ACCOUNT_SUCCESS: string = 'DELETE_ACCOUNT_SUCCESS';
    public static DELETE_ACCOUNT_FAILURE: string = 'DELETE_ACCOUNT_FAILURE';

    public static USER_PROFILE_LOAD_REQUEST: string = 'USER_PROFILE_LOAD_REQUEST';
    public static USER_PROFILE_LOAD_SUCCESS: string = 'USER_PROFILE_LOAD_SUCCESS';
    public static USER_PROFILE_LOAD_FAILURE: string = 'USER_PROFILE_LOAD_FAILURE';

    public static USER_SERVER_SETTINGS_REQUEST: string = 'USER_SERVER_SETTINGS_REQUEST';
    public static USER_SERVER_SETTINGS_SUCCESS: string = 'USER_SERVER_SETTINGS_SUCCESS';
    public static USER_SERVER_SETTINGS_FAILURE: string = 'USER_SERVER_SETTINGS_FAILURE';

    public static SET_USER_SERVER_SETTINGS_LOCALLY: string = 'SET_USER_SERVER_SETTINGS_LOCALLY';
    public static SET_USER_SERVER_SETTINGS: string = 'SET_USER_SERVER_SETTINGS';
    public static SET_USER_SERVER_SETTINGS_REQUEST: string = 'SET_USER_SERVER_SETTINGS_REQUEST';
    public static SET_USER_SERVER_SETTINGS_SUCCESS: string = 'SET_USER_SERVER_SETTINGS_SUCCESS';
    public static SET_USER_SERVER_SETTINGS_FAILURE: string = 'SET_USER_SERVER_SETTINGS_FAILURE';

    // "my activity" view
    public static LOAD_ACTIVITY: string = 'LOAD_ACTIVITY'; // load user activity e.g. "commented on", "deleted", etc.
    public static LOAD_ACTIVITY_REQUEST: string = 'LOAD_ACTIVITY_REQUEST';
    public static LOAD_ACTIVITY_SUCCESS: string = 'LOAD_ACTIVITY_SUCCESS';
    public static LOAD_ACTIVITY_FAILURE: string = 'LOAD_ACTIVITY_FAILURE';

    public static LOAD_PROBLEM: string = 'LOAD_PROBLEM'; // load problem after clicking from "my activity" view
    public static LOAD_PROBLEM_REQUEST: string = 'LOAD_PROBLEM_REQUEST';
    public static LOAD_PROBLEM_SUCCESS: string = 'LOAD_PROBLEM_SUCCESS';
    public static LOAD_PROBLEM_FAILURE: string = 'LOAD_PROBLEM_FAILURE';

    public static LOAD_TICKET: string = 'LOAD_TICKET';
    public static REGENERATE_SUMMARY: string = 'REGENERATE_SUMMARY';
    public static LOAD_TICKET_FROM_COMMENT: string = 'LOAD_TICKET_FROM_COMMENT';

    // feature feedback
    public static SEND_FEATURE_FEEDBACK: string = 'SEND_FEATURE_FEEDBACK';

    // announcements
    public static LOAD_ANNOUNCEMENTS_REQUEST: string = "LOAD_ANNOUNCEMENTS_REQUEST";
    public static LOAD_ANNOUNCEMENTS_SUCCESS: string = "LOAD_ANNOUNCEMENTS_SUCCESS";
    public static LOAD_ANNOUNCEMENTS_FAILURE: string = "LOAD_ANNOUNCEMENTS_FAILURE";
    public static SET_SELECTED_ANNOUNCEMENT: string = "SET_SELECTED_ANNOUNCEMENT";

    // incidents
    public static LOAD_INCIDENTS_REQUEST: string = "LOAD_INCIDENTS_REQUEST";
    public static LOAD_INCIDENTS_SUCCESS: string = "LOAD_INCIDENTS_SUCCESS";
    public static LOAD_INCIDENTS_FAILURE: string = "LOAD_INCIDENTS_FAILURE";

    //Outages : From VS Status
    public static LOAD_VSSTATUS_OUTAGE_REQUEST: string = "LOAD_VSSTATUS_OUTAGE_REQUEST";
    public static LOAD_VSSTATUS_OUTAGE_SUCCESS: string = "LOAD_VSSTATUS_OUTAGE_SUCCESS";
    public static LOAD_VSSTATUS_OUTAGE_FAILURE: string = "LOAD_VSSTATUS_OUTAGE_FAILURE";

    //Custom Outages: From Alerts Table in DB
    public static LOAD_CUSTOM_OUTAGE_REQUEST: string = "LOAD_CUSTOM_OUTAGE_REQUEST";
    public static LOAD_CUSTOM_OUTAGE_SUCCESS: string = "LOAD_CUSTOM_OUTAGE_SUCCESS";
    public static LOAD_CUSTOM_OUTAGE_FAILURE: string = "LOAD_CUSTOM_OUTAGE_FAILURE";

    // commenting
    public static UPDATE_COMMENT_TEXT: string = 'UPDATE_COMMENT_TEXT';
    public static TOGGLE_TOP_LEVEL_COMMENTS: string = 'TOGGLE_TOP_LEVEL_COMMENTS';

    public static ADD_COMMENT: string = 'ADD_COMMENT'; // load comments for the top-level problem
    public static ADD_COMMENT_REQUEST: string = 'ADD_COMMENT_REQUEST';
    public static ADD_COMMENT_SUCCESS: string = 'ADD_COMMENT_SUCCESS';
    public static ADD_COMMENT_FAILURE: string = 'ADD_COMMENT_FAILURE';
    public static UPDATE_COMMENT: string = 'UPDATE_COMMENT';

    public static ADD_SOLUTION: string = 'ADD_SOLUTION';
    public static UPDATE_SOLUTION: string = 'UPDATE_SOLUTION';

    // search
    public static SEARCH_UPDATE: string = 'SEARCH_UPDATE';
    public static SEARCH_UPDATE_UI_CONTROL: string = 'SEARCH_UPDATE_UI_CONTROL';

    // main view
    public static SET_MAIN_VIEW: string = 'SET_MAIN_VIEW';

    public static SET_DIALOG: string = 'SET_DIALOG';
    public static SET_SHOW_CLOSE_WARNING: string = 'SET_SHOW_CLOSE_WARNING';
    public static SET_SHOW_DIALOG_SYSLOGS: string = 'SET_SHOW_DIALOG_SYSLOGS';

    // report a problem dialog (some shared with enhance your vote dialog)
    public static RAP_SET_VIEW: string = 'RAP_SET_VIEW';
    public static RAP_SET_POST_TYPE: string = "RAP_SET_POST_TYPE";
    public static RAP_SET_SPACE_ID: string = "RAP_SET_SPACE_ID";
    public static RAP_SET_ACTION_RECORDER_STATE: string = "RAP_SET_ACTION_RECORDER_STATE";
    public static RAP_SET_ACTION_RECORDER_SAFE_TO_CLOSE_VS: string = "RAP_SET_ACTION_RECORDER_SAFE_TO_CLOSE_VS";
    public static RAP_SET_PARENT_VS_INSTANCE_STATE: string = "RAP_SET_PARENT_VS_INSTANCE_STATE";
    public static RAP_SET_DIAGNOCTIC_SESSION_ID: string = "RAP_SET_DIAGNOCTIC_SESSION_ID";
    public static RAP_SET_TITLE: string = 'RAP_SET_TITLE';
    public static RAP_SET_DETAIL: string = 'RAP_SET_DETAIL';
    public static RAP_SET_WORKED_IN_TEXT: string = 'RAP_SET_WORKED_IN_TEXT';
    public static RAP_SET_SEVERITY_TEXT: string = 'RAP_SET_SEVERITY_TEXT';
    public static RAP_SET_CATEGORY: string = 'RAP_SET_CATEGORY';
    public static RAP_UPDATE_INCLUDE_SCREENSHOT: string = "RAP_UPDATE_INCLUDE_SCREENSHOT";

    public static RAP_INCLUDE_SCREEN_RECORDING: string = "RAP_INCLUDE_SCREEN_RECORDING";
    public static SET_SCREEN_RECORDING_UPLOAD_STATE: string = "SET_SCREEN_RECORDING_UPLOAD_STATE";

    public static RAP_UPDATE_INCLUDE_SYSTEM_LOGS: string = "RAP_UPDATE_INCLUDE_SYSTEM_LOGS";
    public static RAP_UPDATE_IS_REGRESSION: string = "RAP_UPDATE_IS_REGRESSION";
    public static RAP_ADD_FILES_TO_UPLOAD: string = 'RAP_ADD_FILES_TO_UPLOAD';
    public static RAP_REMOVE_FILE_TO_UPLOAD: string = "RAP_REMOVE_FILE_TO_UPLOAD"; //removes selected attachment
    public static RAP_SET_RECORD_PID: string = 'RAP_SET_RECORD_PID'; // selected process to record for
    public static RAP_SET_RUNNING_VS_INSTANCES: string = 'RAP_SET_RUNNING_VS_INSTANCES';
    public static RAP_SET_RECORD_IN_PROGRESS: string = 'RAP_SET_RECORD_IN_PROGRESS';
    public static RAP_DETERMINE_IS_PUBLIC_BUILD_REQUEST: string = "RAP_DETERMINE_IS_PUBLIC_BUILD_REQUEST";
    public static RAP_DETERMINE_IS_PUBLIC_BUILD_SUCCESS: string = "RAP_DETERMINE_IS_PUBLIC_BUILD_SUCCESS";
    public static RAP_DETERMINE_IS_PUBLIC_BUILD_FAILURE: string = "RAP_DETERMINE_IS_PUBLIC_BUILD_FAILURE";

    public static SET_SETTINGS_UPDATE: string = "SET_SETTINGS_UPDATE";

    // system files
    public static RAP_GET_SYSTEM_FILES_REQUEST: string = "RAP_GET_SYSTEM_FILES_REQUEST";
    public static RAP_GET_SYSTEM_FILES_SUCCESS: string = "RAP_GET_SYSTEM_FILES_SUCCESS";
    public static RAP_GET_SYSTEM_FILES_FAILURE: string = "RAP_GET_SYSTEM_FILES_FAILURE";

    // report problem action (shared with enhance your vote dialog)
    public static RAP_SUBMIT: string = "RAP_SUBMIT";
    public static RAP_SUBMIT_REQUEST: string = "RAP_SUBMIT_REQUEST";
    public static RAP_SUBMIT_SUCCESS: string = "RAP_SUBMIT_SUCCESS";
    public static RAP_SUBMIT_FAILURE: string = "RAP_SUBMIT_FAILURE";

    public static RAP_CLEAR_SUBMITTED_POST_RECENTLY: string = "RAP_CLEAR_SUBMITTED_POST_RECENTLY";

    public static RAP_CLEAR_FORM: string = "RAP_CLEAR_FORM";

    // crop
    public static RAP_SET_ISCROPPING = "RAP_SET_ISCROPPING";
    public static RAP_SET_IS_CROP_PROMPT_VISIBLE = "RAP_SET_IS_CROP_PROMPT_VISIBLE";
    public static RAP_SET_CROP_REGION = "RAP_SET_CROP_REGION";

    // enhance your vote
    public static EV_SET_COMMENT = "EV_SET_COMMENT";

    // need more info
    public static NMI_SET_COMMENT = "NMI_SET_COMMENT";
    public static NMI_COMMENT_REQUEST: string = 'NMI_COMMENT_REQUEST';
    public static NMI_COMMENT_SUCCESS: string = 'NMI_COMMENT_SUCCESS';
    public static NMI_COMMENT_FAILURE: string = 'NMI_COMMENT_FAILURE';
    public static LOAD_NMI_COMMENTS_REQUEST: string = 'LOAD_NMI_COMMENTS_REQUEST';
    public static LOAD_NMI_COMMENTS_SUCCESS: string = 'LOAD_NMI_COMMENTS_SUCCESS';
    public static LOAD_NMI_COMMENTS_FAILURE: string = 'LOAD_NMI_COMMENTS_FAILURE';
    public static TOGGLE_NMI_COMMENTS: string = 'TOGGLE_NMI_COMMENTS';

    // sign in
    public static SET_SIGN_IN_STATE = "SET_SIGN_IN_STATE";
    public static SET_SIGN_IN_SUCCESS_ACTION = "SET_SIGN_IN_SUCCESS_ACTION";
    public static SET_ACCOUNTS: string = "SET_ACCOUNTS";

    public static SET_HAS_SEARCHED = "SET_HAS_SEARCHED";
    public static SET_SEARCH_NOT_STARTED = "SET_SEARCH_NOT_STARTED"; // if  MS internal, used to not show results or start searching until signed in

    public static SET_ACTIVE_FOLLOW_TAB = "SET_ACTIVE_FOLLOW_TAB";

    public static SET_ACTIVE_SEARCH_TAB = "SET_ACTIVE_SEARCH_TAB";

    // text editing
    public static SET_USED_FORMATTING = "SET_USED_FORMATTING";

    // tags
    public static GET_TAGS: string = 'GET_TAGS';

    // ticket details
    public static FOLLOW_TICKET = "FOLLOW_TICKET";
    public static UNFOLLOW_TICKET = "UNFOLLOW_TICKET";

    public static FLAG_FOR_MODERATION = "FLAG_FOR_MODERATION";

    public static CONVERT_NODE = "CONVERT_NODE";
    public static VOTE_ON_NODE = "VOTE_ON_NODE";
    public static REACTIVATE_NODE = "REACTIVATE_NODE";
    public static DELETE_NODE = "DELETE_NODE";

    public static UPDATE_TICKET = "UPDATE_TICKET";

    public static RELEASE_ACTION_INFO_REQUEST: string = 'RELEASE_ACTION_INFO_REQUEST';
    public static RELEASE_ACTION_INFO_SUCCESS: string = 'RELEASE_ACTION_INFO_SUCCESS';
    public static RELEASE_ACTION_INFO_FAILURE: string = 'RELEASE_ACTION_INFO_FAILURE';

    public static GET_APPROVED_FILE_EXTENSIONS: string = 'GET_APPROVED_FILE_EXTENSIONS';

    // admin page and configuration
    public static GET_ROUTING_RULES: string = 'GET_ROUTING_RULES';
    public static GET_AUDIT_LOGS: string = 'GET_AUDIT_LOGS';
    public static GET_FEATURE_FLAGS: string = 'GET_FEATURE_FLAGS';
    public static GET_MOD_ADMIN_USERS: string = 'GET_MOD_ADMIN_USERS';
    public static GET_SITE_SPACE_CONFIG: string = 'GET_SITE_SPACE_CONFIG';
    public static GET_PIPELINE_STATUS: string = 'GET_PIPELINE_STATUS';
    public static GET_GDPR_STATUS: string = 'GET_GDPR_STATUS';
    public static UPDATE_SPACE_CONFIG: string = 'UPDATE_SPACE_CONFIG';
    public static UPDATE_WEBSITE_CONFIG: string = 'UPDATE_WEBSITE_CONFIG';
    public static UPDATE_FEATURE_FLAG: string = 'UPDATE_FEATURE_FLAG';
}

export enum HttpResponse {
    Conflict = 409, // used for "already voted" scenario
    Unauthorized = 401, // used for detecting if token issues on reporting a problem
}

export interface StandardAction<T = any> {
    type: string;
    payload: T;
}

export const setSiteReadOnlySetting = (): void => {
    getLocalCall(false, LocalServerApis.ReadOnlyDevCom, Actions.SET_SITE_READONLY_SETTING);
};

export const setAppSettings = (appSettings: Settings): void => {
    App.store.dispatch({
        type: Actions.SET_APP_SETTINGS,
        payload: {
            appSettings
        }
    });
};

// Passing undefined for params will leave them untouched in store, passing null will clear them out
export const setOperationState = <T = any>(key: string, state: UIOperationState = undefined, data: T = undefined, message: string = undefined): void => {
    App.store.dispatch({
        type: Actions.SET_OPERATION_STATE,
        payload: {
            key: key,
            state,
            data,
            message,
        }
    });
};

export const disconnectedFromNativeProcess = (isDisconnected: boolean): void => {
    App.store.dispatch({
        type: Actions.DISCONNECTED_FROM_NATIVE_PROCESS,
        payload: {
            isDisconnected
        }
    });
};

export const setGlobalNotification = (notificationInfo: NotificationInfo): void => {
    App.store.dispatch({
        type: Actions.SET_GLOBAL_NOTIFICATION,
        notificationInfo
    });
};

export const clearGlobalNotification = (): void => {
    App.store.dispatch({
        type: Actions.SET_GLOBAL_NOTIFICATION,
        notificationInfo: null
    });
};

export const setContextualNotification = (key: string, notificationInfo: NotificationInfo): void => {
    App.store.dispatch({
        type: Actions.SET_CONTEXTUAL_NOTIFICATION,
        key,
        notificationInfo
    });
};

export const clearContextualNotification = (key: string): void => {
    App.store.dispatch({
        type: Actions.SET_CONTEXTUAL_NOTIFICATION,
        key,
        notificationInfo: null
    });
};

export const clearAllContextualNotifications = (): void => {
    App.store.dispatch({
        type: Actions.CLEAR_ALL_CONTEXTUAL_NOTIFICATION,
    });
};

export const setUsedFormatting = (usedFormatting: boolean): void => {
    App.store.dispatch({
        type: Actions.SET_USED_FORMATTING,
        value: usedFormatting,
    });
};

export const setActiveFollowTab = (tab: FollowTab): void => {
    App.store.dispatch({
        type: Actions.SET_ACTIVE_FOLLOW_TAB,
        tab,
    });
};

export const setActiveSearchTab = (tab: SearchTab): void => {
    App.store.dispatch({
        type: Actions.SET_ACTIVE_SEARCH_TAB,
        tab,
    });
};

export const setHasSearchedTrue = (): void => {
    App.store.dispatch({
        type: Actions.SET_HAS_SEARCHED,
    });
};

export const setSignInSuccessAction = (action: null | (() => void)): void => {
    App.store.dispatch({
        type: Actions.SET_SIGN_IN_SUCCESS_ACTION,
        action,
    });
};

export const setSignInState = (signInState: SignInState,
    errorMessage: string = "",
    userName: string = "",
    userEmail: string = "",
    userVsid: string = "",
    userAccountType?: AccountType,
    userLogo: string | null = null): void => {

    // check for successful sign in, execute success action if present, then clear it
    if (signInState === SignInState.Success) {
        const appState: AppState = App.store.getState().appState;
        if (appState.signInSuccessAction) {
            appState.signInSuccessAction();
            setSignInSuccessAction(null);
        }
    }

    App.store.dispatch({
        type: Actions.SET_SIGN_IN_STATE,
        value: signInState,
        errorMessage,
        userName,
        userEmail,
        userVsid,
        userAccountType: userAccountType ?? AccountType.msa, // if unknown origin (for whatever reason), treat it as MSA - same as older MeControlV1 code
        userLogo,
    });

    if (instance.isInternal && signInState === SignInState.Prompt) {
        // redirect to sign in page
        App.requestSignOutAndIn();
    }
};

export const setAccounts = (accounts: UserInfo[]): void => {
    App.store.dispatch({
        type: Actions.SET_ACCOUNTS,
        accounts,
    });
};

export const setSearchNotStarted = (target: SearchInfoTarget): void => {
    App.store.dispatch({
        type: Actions.SET_SEARCH_NOT_STARTED,
        target,
    });
};

export const setIsCropPromptVisible = (isCropPromptVisible: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_IS_CROP_PROMPT_VISIBLE,
        value: isCropPromptVisible,
    });
};

export const setIsCropping = (isCropping: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_ISCROPPING,
        value: isCropping,
    });
};

export const setCropRegion = (cropRegion: CropRect | null, croppedImageDataUrl: string | null): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_CROP_REGION,
        value: cropRegion,
        croppedImageDataUrl,
    });
};

export const toggleTopLevelComments = (): void => {
    App.store.dispatch({
        type: Actions.TOGGLE_TOP_LEVEL_COMMENTS,
    });
};

export const clearEvents = (): void => {
    App.store.dispatch({
        type: Actions.CLEAR_EVENTS,
    });
};

// For controlling whether we show or not the Close button on the ReportAProblem
export const setCloseable = (isCloseable: boolean): void => {
    App.store.dispatch({
        type: Actions.SET_CLOSEABLE,
        value: isCloseable,
    });
};

export const setShowGuidance = (shouldShowGuidance: boolean): void => {
    App.store.dispatch({
        type: Actions.SET_SHOW_GUIDANCE,
        value: shouldShowGuidance,
    });
};

export const setSort = (value: string): void => {
    App.store.dispatch({
        type: Actions.SET_SORT,
        value: value,
    });
};

export const setFilter = (value: string): void => {
    App.store.dispatch({
        type: Actions.SET_FILTER,
        value: value,
    });
};

export const setTypeFilter = (value: TypesFilterFlags): void => {
    App.store.dispatch({
        type: Actions.SET_TYPE_FILTER,
        value: value,
    });
};

export const searchComplete = (searchtext: string): void => {
    Actions.lastSuggestId++;
    const currSuggestId: number = Actions.lastSuggestId;

    searchtext = searchtext.substr(0, 100);

    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    const template: any = JSON.parse(JSON.stringify(App.settings.template));
    template.title = searchtext;

    // don't sent dotnet and watson data on every search - it's not needed and can be big
    template.dotNetEntries = [];
    template.watsonEntries = [];

    const state: AppState = getState();

    let flags: TypesFilterFlags = TypesFilterFlags.defaultProblems;

    switch (state.dialog) {
        case Dialog.none:
            flags = TypesFilterFlags.all;
            break;
        case Dialog.add:
            flags = TypesFilterFlags.problem;
            break;
        case Dialog.suggest:
            flags = TypesFilterFlags.idea;
            break;
        default:
            throw new Error(`${state.dialog} is an invalid dialog state for suggest.`);
    }

    const data = {
        searchFor: template,
        skip: 0,
        take: 10,
        sort: state.sort,
        filter: 'all',
        typesFilterFlags: flags,
        clientVersion: clientVersion,
        capabilities: App.settings.capabilities,
    };

    dispatch({
        type: Actions.SUGGEST,
        currSuggestId,
        promise: platformActions.post(false, Apis.SearchQuerySearchComplete, data),
    });
};

export class SearchOptions {
    public skip: number = 0;
    public take: number = 20;
    public sort: Sort | null = null;
    public filter: Filter | null = null;
    public returnTotalCount: boolean = false;
    public updateCountsByType: boolean = false;
    public type: TypesFilterFlags | null = null;
    public stateGroup: StateGroupFilter | null = null;
    public timeFilter: number = 0;
    public spaceIds: string[];
    public notAdditive: boolean;       // true for 'load more' kind of search, false for paged kind of search
}

// We don't filter by user's build, channel etc. anymore during search so ignore these tags in search to avoid issues with search for non-VS spaces when connected to VS
const templateTagTypesToIgnoreForSearch = ["product-version", "product", "channel-id", "channel-manifest-id", "product-build", "product-build-branch", "product-display-version", "setup-branch-version"];

export const searchFor = (searchtext: string,
    target: SearchInfoTarget,
    searchOptions: SearchOptions = new SearchOptions(),
    searchSource: string = null,   // Page/component where the search originated
): void => {
    // if (searchtext == '') searchtext = 'vsftest'; // uncomment for testing
    Actions.lastSearchId++;
    const currSearchId: number = Actions.lastSearchId;
    const userQuery: TagsResult = App.getTags(searchtext);

    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    dispatch({
        type: Actions.SEARCH_REQUEST,
        userQuery: userQuery,
        currSearchId: currSearchId,
        skip: searchOptions.skip,
        take: searchOptions.take,
        target: target,
        notAdditive: searchOptions.notAdditive,
        searchSource,
    });

    const template: any = JSON.parse(JSON.stringify(App.settings.template));
    template.title = userQuery.textNoTags;

    // don't sent dotnet and watson data on every search - it's not needed and can be big
    template.dotNetEntries = [];
    template.watsonEntries = [];

    template.tags = hasItems(template.tags)
        ? template.tags.filter((tag: FeedbackTag) => templateTagTypesToIgnoreForSearch.indexOf(tag.type) === -1)
        : template.tags;

    const state: AppState = getState();

    let flags: TypesFilterFlags = TypesFilterFlags.defaultProblems;

    const shouldAuth: boolean = [
        SearchInfoTarget.myActivity,
        SearchInfoTarget.myProblems,
        SearchInfoTarget.mySuggestions,
        SearchInfoTarget.myNMIItems
    ].indexOf(target) !== -1;

    if (searchOptions.type) {
        flags = searchOptions.type;
    } else {
        switch (target) {
            case SearchInfoTarget.all:
                flags = TypesFilterFlags.all;
                break;
            case SearchInfoTarget.problems:
                flags = TypesFilterFlags.problem;
                break;
            case SearchInfoTarget.suggestions:
                flags = TypesFilterFlags.idea;
                break;
            case SearchInfoTarget.myActivity:
                flags = TypesFilterFlags.all;
                break;
            case SearchInfoTarget.myProblems:
                flags = TypesFilterFlags.problem;
                break;
            case SearchInfoTarget.mySuggestions:
                flags = TypesFilterFlags.idea;
                break;
            case SearchInfoTarget.myNMIItems:
                flags = TypesFilterFlags.all;
                break;
            default:
                throw new Error(`SearchInfoTarget ${target} is not recognized. When adding new search targets make sure to update this code.`);
        }
    }

    const data = {
        searchFor: template,
        skip: searchOptions.skip,
        take: searchOptions.take || 20,
        sort: searchOptions.sort || state.sort,
        filter: searchOptions.filter || state.filter,
        spaceIds: searchOptions.spaceIds,
        stateGroup: searchOptions.stateGroup,
        version: 3,
        topicsToFilterBy: userQuery.tags,
        returnTotalCount: searchOptions.returnTotalCount,
        typesFilterFlags: flags,
        includeCountsByType: searchOptions.updateCountsByType,
        searchStartTimeMsec: Date.now(),
        searchSource,
        clientVersion: clientVersion,
        capabilities: App.settings.capabilities,
        userName: userQuery.userName,
        timeFilter: searchOptions.timeFilter,
    };

    // console.log("************************************");
    // console.log(data);
    // console.log("************************************");

    platformActions.post(shouldAuth, Apis.SearchQuerySearchV2, data).then((req) => {
        dispatch({
            type: Actions.SEARCH_SUCCESS,
            data: data,
            req: req,
            userQuery: userQuery,
            currSearchId: currSearchId,
            skip: searchOptions.skip,
            target: target,
            notAdditive: searchOptions.notAdditive,
        });
    }).catch((error) => {
        dispatch({
            type: Actions.SEARCH_FAILURE,
            data: data,
            userQuery: userQuery,
            currSearchId: currSearchId,
            skip: searchOptions.skip,
            target: target,
            notAdditive: searchOptions.notAdditive,
            errorMessage: stringFromErrorOrAny(error),
        });
    });
};

/**
 * AI/ML embedding model config ID. Used to differentiate which model and parameters generated a specific embedding vector
 * in the VectorCache SQL table.
 */
export enum ModelConfigId {
    /**
     * Default value for search rest API
     */
    None = 0,

    /**
     * `text-embedding-ada-002` model from OpenAI with default parameters.
     * nikolami: 2025-04-16: deprecated, since text3_... embeddings are better
     */
    DEPRECATED_Ada002 = 1,

    /**
     * `text-embedding-ada-002` model from OpenAI, posts are summarized to single line before embedding.
     * nikolami: 2025-04-16: deprecated, since text3_... embeddings are better
     */
    DEPRECATED_Ada002_SummarizedOneLineEmbedding = 2,

    /**
     * `text-embedding-3-large` model from OpenAI with default parameters and 3072 embedding size.
     */
    Text3_Large = 3,

    /**
     * `text-embedding-3-large` model from OpenAI, posts are summarized to single line before embedding. 3072 embedding size. nikolami: 2025-04-10: best efficiency so far
     */
    Text3_Large_SummarizedOneLineEmbedding = 4,

    /**
     * `text-embedding-3-small` model from OpenAI with default parameters and 1536 embedding size.
     */
    Text3_Small = 5,

    /**
     * `text-embedding-3-small` model from OpenAI, posts are summarized to single line before embedding. 1536 embedding size.
     */
    Text3_Small_SummarizedOneLineEmbedding = 6,

    /**
     * Current default model config ID for new embeddings.
     */
    Current = Text3_Large_SummarizedOneLineEmbedding,
}

export const vectorSearch = (title: string,
    text: string,
    spaceId: SpaceId | null = null,
    searchSource: string | null = null,   // Page/component where the search originated
): void => {
    Actions.lastSearchId++;
    const currSearchId: number = Actions.lastSearchId;

    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    dispatch({
        type: Actions.VECTOR_SEARCH_REQUEST,
        title,
        text,
        currSearchId: currSearchId,
        searchSource,
    });

    const template: any = JSON.parse(JSON.stringify(App.settings.template));
    template.title = title;
    template.text = text;

    // don't sent dotnet and watson data on every search - it's not needed and can be big
    template.dotNetEntries = [];
    template.watsonEntries = [];

    template.tags = hasItems(template.tags)
        ? template.tags.filter((tag: FeedbackTag) => templateTagTypesToIgnoreForSearch.indexOf(tag.type) === -1)
        : template.tags;

    const state: AppState = getState();

    const data = {
        searchFor: template,
        spaceIds: spaceId ? [spaceId.toString()] : null,
        version: 3,
        returnTotalCount: false,
        includeCountsByType: false,
        searchStartTimeMsec: Date.now(),
        searchSource,
        clientVersion: clientVersion,
        capabilities: App.settings.capabilities,

        model: ModelConfigId.Current,

        skip: 0,
        take: 5,

        sort: Sort.relevance,
        filter: Filter.all,
        timeFilter: -12,
    };

    // console.log("************************************");
    // console.log(data);
    // console.log("************************************");

    platformActions.post(true, Apis.SearchQuerySearchV2, data).then((req) => {
        dispatch({
            type: Actions.VECTOR_SEARCH_SUCCESS,
            data: data,
            req: req,
            currSearchId: currSearchId,
        });
    }).catch((error) => {
        dispatch({
            type: Actions.VECTOR_SEARCH_FAILURE,
            data: data,
            currSearchId: currSearchId,
            errorMessage: stringFromErrorOrAny(error),
        });
    });
};

export const searchUpdateUiControl = (searchtext: string): void => {
    App.store.dispatch({
        type: Actions.SEARCH_UPDATE_UI_CONTROL,
        searchtext: searchtext,
    });
};

export const searchUpdate = (searchtext: string): void => {
    App.store.dispatch({
        type: Actions.SEARCH_UPDATE,
        searchtext: searchtext,
    });
};

export const updateCommentText = (commentText: string): void => {
    App.store.dispatch({
        type: Actions.UPDATE_COMMENT_TEXT,
        commentText: commentText,
    });
};

export const cancelComment = (): void => {
    App.store.dispatch({
        type: Actions.UPDATE_COMMENT_TEXT,
        commentText: "",
    });
};

export const toggleNmiComments = (): void => {
    App.store.dispatch({
        type: Actions.TOGGLE_NMI_COMMENTS,
    });
};

export const nmiUpdateCommentText = (nmiComment: string): void => {
    App.store.dispatch({
        type: Actions.NMI_SET_COMMENT,
        nmiComment: nmiComment,
    });
};

export const addComment = (addCommentRequest: AddCommentRequest): void => {
    const { item, commentText: commentTextOriginal, parentId, visibility, newNodeTrackingId, operationKey, includeNativeAttachments } = addCommentRequest;
    const isProvideInfo = addCommentRequest.isProvideInfo ?? false;
    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    const state: AppState = getState();
    const vsid: string = App.getVsid(App.settings.template);

    const data = {
        requestVersion: 1,
        parentId: parentId,
        userIdMakingRequest: vsid,
        comment: commentTextOriginal,
        isInfoProvided: isProvideInfo,
        clientVersion: clientVersion,
        trackingId: newNodeTrackingId,
        visibility: visibility,
        submissionStartTimeMsec: Date.now(),
        capabilities: App.settings.capabilities,
    };

    if (isProvideInfo === false) {
        dispatch({ type: Actions.ADD_COMMENT_REQUEST, data, item, operationKey });
    }
    else {
        dispatch({ type: Actions.NMI_COMMENT_REQUEST, data, item, operationKey });
    }

    platformActions.post(true, Apis.DetailsV2SubmitComment, data).then((req) => {
        dispatch({ type: isProvideInfo === false ? Actions.ADD_COMMENT_SUCCESS : Actions.NMI_COMMENT_SUCCESS, data, req, item, operationKey });
        if (isProvideInfo !== false || includeNativeAttachments) {
            const commentTrackingId: string = (req as any).data.trackingId;
            if (commentTrackingId == null || commentTrackingId === "") {
                return;
            }
            App.startUploadingFiles(commentTrackingId, state);
        }
    }).catch((reason: any) => {
        console.log(reason);
        if (isProvideInfo === false) {
            dispatch({ type: Actions.ADD_COMMENT_FAILURE, data, item, operationKey, error: reason, errorMessage: stringFromErrorOrAny(reason) });

            setGlobalNotification({
                type: "error",
                canDismiss: true,
                title: ResourceStrings.unexpectedError(),
                message: ResourceStrings.errorOnSend()
            } as NotificationInfo);
        }
        else {
            dispatch({ type: Actions.NMI_COMMENT_FAILURE, data, item, operationKey, error: reason, errorMessage: stringFromErrorOrAny(reason) });

            setGlobalNotification({
                type: "error",
                canDismiss: true,
                title: ResourceStrings.unexpectedError(),
                message: ResourceStrings.errorOnSend()
            } as NotificationInfo);
        }
    });
};

export const updateComment = (ticket: RootPost, commentId: number, parentId: number, updatedBody: string, trackingId: string, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        postId: commentId,
        description: updatedBody,
    };

    dispatch({ type: requestAction(Actions.UPDATE_COMMENT), data, ticketId: ticket.id, commentId, operationKey });

    platformActions.post(true, Apis.NodeUpdate, data).then((req) => {
        dispatch({ type: successAction(Actions.UPDATE_COMMENT), data, ticketId: ticket.id, commentId, req, operationKey });
    }).catch((reason: any) => {
        console.log(reason);
        dispatch({ type: failureAction(Actions.UPDATE_COMMENT), data, reason, ticketId: ticket.id, commentId, operationKey });
    });
};

export const addSolutionOrWorkaround = (ticket: RootPost, text: string, trackingId: string, operationKey: string, isWorkaround: boolean): void => {
    const dispatch = App.store.dispatch;

    const data = {
        requestVersion: 1,
        parentId: ticket.id,
        solution: text,
        trackingId,
        postType: !isWorkaround ? PostType.solution : PostType.workaround,
    };

    dispatch({ type: requestAction(Actions.ADD_SOLUTION), data, ticketId: ticket.id, operationKey, isWorkaround });

    platformActions.post(true, Apis.DetailsV2SubmitSolution, data).then((req) => {
        dispatch({ type: successAction(Actions.ADD_SOLUTION), data, req, ticketId: ticket.id, operationKey, isWorkaround });
    }).catch((reason: any) => {
        console.log(reason);
        dispatch({ type: failureAction(Actions.ADD_SOLUTION), data, error: reason, ticketId: ticket.id, operationKey, isWorkaround, errorMessage: stringFromErrorOrAny(reason) });
    });
};

export const updateSolutionOrWorkaround = (ticket: RootPost, solutionId: number, updatedBody: string, trackingId: string, operationKey: string, isWorkaround: boolean): void => {
    const dispatch = App.store.dispatch;

    const data = {
        postId: solutionId,
        description: updatedBody,
    };

    dispatch({ type: requestAction(Actions.UPDATE_SOLUTION), data, ticketId: ticket.id, solutionId, operationKey, isWorkaround });

    platformActions.post(true, Apis.NodeUpdate, data).then((req) => {
        dispatch({ type: successAction(Actions.UPDATE_SOLUTION), data, ticketId: ticket.id, solutionId, req, operationKey, isWorkaround });
    }).catch((reason: any) => {
        console.log(reason);
        dispatch({ type: failureAction(Actions.UPDATE_SOLUTION), data, error: reason, ticketId: ticket.id, solutionId, operationKey, isWorkaround, errorMessage: stringFromErrorOrAny(reason) });
    });
};

export function filterFilesToUploadIgnoreErrors(): void {
    if ((App.settings.filesToUploadPerAssembly == null) ||
        !Array.isArray(App.settings.filesToUploadPerAssembly) ||
        (App.settings.filesToUploadPerAssembly.length === 0)) { // if nothing to filter
        return;
    }

    platformActions.post(false, Apis.DiagnosticsApproveFilesToUpload, {
        filesToUploadPerAssembly: App.settings.filesToUploadPerAssembly,
    }).then((req) => {
        const results: string[] | null = req.data as any;

        if ((results == null) ||
            !Array.isArray(results) ||
            (results.length === 0)) {
            return;
        }

        if (App.settings.filesToUpload == null) {
            App.settings.filesToUpload = [];
        }

        const existingFiles: Set<string> = new Set<string>();
        App.settings.filesToUpload.forEach((filePath: string) => {
            existingFiles.add(filePath);
        });

        // add filtered results to files to upload
        results.forEach((filePath: string) => {
            if (existingFiles.has(filePath)) {
                return;
            }

            existingFiles.add(filePath);
            App.settings.filesToUpload.push(filePath);
        });

        // syslogs list in app.settings has been modified. sync it to redux store
        // @TODO: Ideally just keep one source of truth i.e. redux store
        rapGetSystemLogsToUpload();
    }).catch((error) => {
        // ignore errors
        console.error("ERROR filtering files");
        console.log(error);
    });
}

export const loadTicket = (postId: number): void => {
    getCall(false, `${Apis.DetailsV3RootPost}?id=${postId}`, Actions.LOAD_TICKET, { postId });
};

/** Load or regenerate AI summary. */
export const regenerateSummary = (postId: PostId, isFirstLoad: boolean): void => {
    // auth is required to regenerate summary, but not on first load
    const regenParam = isFirstLoad ? "" : "&regen=true";
    getCall(!isFirstLoad, `${Apis.DetailsV3Summary}?id=${postId}${regenParam}`, Actions.REGENERATE_SUMMARY, { postId });
};

export const sendFeatureFeedback = (
    feature: FeatureName,
    sentiment: FeatureSentiment,
    comment: string | null,
    aiSummaryId?: number
): void => {
    postCall(false, Apis.DetailsV3FeatureFeedback, Actions.SEND_FEATURE_FEEDBACK, { feature, sentiment, comment, aiSummaryId });
}

export const loadTicketFromDescendantComment = (descendantCommentId: number | null): void => {
    const dispatch = App.store.dispatch;

    const template: any = JSON.parse(JSON.stringify(App.settings.template));
    template.title = descendantCommentId.toString();

    // don't send dotnet and watson data on every search - it's not needed and can be big
    template.dotNetEntries = [];
    template.watsonEntries = [];

    const data = {
        searchFor: template,
        skip: 0,
        take: 1,
        sort: Sort.relevance,
        filter: Filter.specialComment,
        version: 3,
        topicsToFilterBy: [],
        returnTotalCount: false,
        typesFilterFlags: TypesFilterFlags.all,
        clientVersion: clientVersion,
        capabilities: App.settings.capabilities,
    };

    dispatch({
        type: Actions.LOAD_TICKET_FROM_COMMENT,
        data: { descendantCommentId },
        promise: new Promise((resolve, reject) => {
            platformActions.post(false, Apis.SearchQueryItem, data)
                .then(req => {
                    const results: SearchResults = req.data as any;

                    if ((results.results) && (results.results.length > 0)) {
                        const ticket: SearchResult = results.results[0];
                        ticket.textRaw = ticket.text;
                        ticket.text = App.HtmlFromMarkdownIfNeeded(ticket.text);
                        resolve({ descendantCommentId, ticket });
                    }
                    reject(`If you recently created this ticket, our systems are still syncing.  Please refresh in 15-20 minutes and the ticket details should be available. Ticket with comment id '${descendantCommentId}' not found`);
                })
                .catch(error => {
                    reject(error);
                });
        })
    });
};

export const initDetailsView = (item: SearchResult): void => {
    Actions.lastSelectedProblemId = item.answerHubId;

    const dispatch = App.store.dispatch;

    dispatch({
        type: Actions.INIT_DETAILS_VIEW,
        item: item,
        postId: item.answerHubId,
    });
}

export const setDialog = (dialog: Dialog): void => {
    App.store.dispatch({
        type: Actions.SET_DIALOG,
        dialog: dialog,
    });
};

export const setShowCloseWarning = (shouldShowCloseWarning: boolean): void => {
    App.store.dispatch({
        type: Actions.SET_SHOW_CLOSE_WARNING,
        shouldShowCloseWarning: shouldShowCloseWarning,
    });
};

export const setShowDialogSysLogs = (shouldShow: boolean): void => {
    App.store.dispatch({
        type: Actions.SET_SHOW_DIALOG_SYSLOGS,
        shouldShow: shouldShow,
    });
};

export const setMainView = (view: MainView): void => {
    App.store.dispatch({
        type: Actions.SET_MAIN_VIEW,
        view: view,
    });
};

export const setRapView = (view: RapView): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_VIEW,
        view: view,
    });
};

export const setRapPostType = (postType: EntryValuePostType): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_POST_TYPE,
        postType,
    });
};

export const setRapSpaceId = (spaceId: SpaceId): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_SPACE_ID,
        spaceId,
    });
};

export const setdiagSessionId = (diagSessionId: number): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_DIAGNOCTIC_SESSION_ID,
        diagSessionId
    });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const setActionRecorderState = (state: ActionRecorderState, data?: any): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_ACTION_RECORDER_STATE,
        state: state,
        data
    });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const setActionRecorderSafeToCloseVS = (data?: any): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_ACTION_RECORDER_SAFE_TO_CLOSE_VS,
        data
    });
};

export const setParentVSInstanceRunningState = (isParentVSInstanceRunning: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_PARENT_VS_INSTANCE_STATE,
        isParentVSInstanceRunning
    });
};

export const rapSetTitle = (title: string): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_TITLE,
        title: title,
    });
};

export const rapSetDetail = (detail: string): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_DETAIL,
        detail: detail,
    });
};

export const rapSetWorkedInText = (text: string): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_WORKED_IN_TEXT,
        text: text,
    });
};

export const rapSetSeverityText = (text: string): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_SEVERITY_TEXT,
        text: text,
    });
};

export const rapSetCategory = (key: string): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_CATEGORY,
        key: key,
    });
};

export const rapAddFilesToUpload = (files: FileInfo[]): void => {
    App.store.dispatch({
        type: Actions.RAP_ADD_FILES_TO_UPLOAD,
        files: files,
    });
};

export const rapRemoveFileToUpload = (file: FileInfo): void => {
    App.store.dispatch({
        type: Actions.RAP_REMOVE_FILE_TO_UPLOAD,
        file: file,
    });
};

export const rapGetSystemLogsToUpload = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.RAP_GET_SYSTEM_FILES_REQUEST });
    try {
        const systemLogs: FileInfo[] = App.getSystemLogsToUpload();
        App.store.dispatch({
            type: Actions.RAP_GET_SYSTEM_FILES_SUCCESS,
            files: systemLogs,
        });
    }
    catch {
        App.store.dispatch({ type: Actions.RAP_GET_SYSTEM_FILES_FAILURE });
    }
};

export const rapSetRecordPid = (value: number): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_RECORD_PID,
        value: value,
    });
};

export const setSettingsUpdate = (value: SettingsUpdate): void => {
    App.store.dispatch({
        type: Actions.SET_SETTINGS_UPDATE,
        value: value,
    });
};

export const rapSetRecordingInProgress = (value: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_RECORD_IN_PROGRESS,
        value: value,
    });
};

export const rapAddRunningVSInstances = (processes: VSInstanceInfo[]): void => {
    App.store.dispatch({
        type: Actions.RAP_SET_RUNNING_VS_INSTANCES,
        processes: processes,
    });
};

export const rapUpdateIncludeScreenshot = (shouldInclude: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_UPDATE_INCLUDE_SCREENSHOT,
        shouldInclude: shouldInclude,
    });
};

export const rapIncludeScreenRecording = (shouldInclude: boolean, video: Blob | null): void => {
    App.store.dispatch({
        type: Actions.RAP_INCLUDE_SCREEN_RECORDING,
        shouldInclude: shouldInclude,
        video: video,
    });
};

export const rapSetScreenRecordingUploadState = (state: OperationState, progressPercent: number, error?: string): void => {
    App.store.dispatch({
        type: Actions.SET_SCREEN_RECORDING_UPLOAD_STATE,
        state,
        progressPercent,
        error,
    });
};

export const rapUpdateIncludeSystemLogs = (shouldInclude: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_UPDATE_INCLUDE_SYSTEM_LOGS,
        shouldInclude: shouldInclude,
    });
};

export const rapUpdateIsRegression = (shouldSetAsRegression: boolean): void => {
    App.store.dispatch({
        type: Actions.RAP_UPDATE_IS_REGRESSION,
        shouldSetAsRegression: shouldSetAsRegression,
    });
};

export const evUpdateCommentText = (evComment: string): void => {
    App.store.dispatch({
        type: Actions.EV_SET_COMMENT,
        evComment: evComment,
    });
};

const updateReservedTrackingId = (newTrackingId: string | null): void => {
    App.settings.template.trackingId = newTrackingId;
    App.updateSettings(App.settings);

    // @TODO: uncomment once Redux store includes app settings
    // public static RAP_RESERVE_TRACKING_ID: string = "RAP_RESERVE_TRACKING_ID";
    // App.store.dispatch({
    //     type: Actions.RAP_RESERVE_TRACKING_ID,
    //     newTrackingId: newTrackingId,
    // });
}

/** Used to reserve new tracking ID in App.settings.template.trackingId if supported
 * The tracking ID is used to track Nexus session file upload
 * Introduced in Nexus session support for reporting more than one problem from the same session.
 * Needed whether Nexus session or not.
 */
export const tryReserveNewTrackingId = (): void => {
    if (!App.settings.nativeCapabilities.canReserveNewTrackingId) {
        updateReservedTrackingId(generateTrackingId());
        return;
    }

    const native: NativeActions = platformActions.getNativeActions();
    if (native == null) {
        updateReservedTrackingId(generateTrackingId());
        return;
    }

    native.reserveNewTrackingId().then((info: TrackingIdInfo) => {
        updateReservedTrackingId(info.trackingId);
    }).catch(reason => {
        console.error(reason);
        updateReservedTrackingId(generateTrackingId());
    });
}

export const voteFor = (item: PartialPost, isVoteEnhanced: boolean = false, commentText: string = "", operationKey: string = null): void => {
    const voteStartTimeMsec = Date.now();
    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    const state: AppState = getState();

    const data = JSON.parse(JSON.stringify(App.settings.template));
    tryReserveNewTrackingId(); // reserve new tracking ID to support reporting another problem (introduced with Nexus session support)

    const commentTrackingId: string = data.trackingId;  // this is the unique tracking id generated for this RAP session

    data.parentId = item.trackingId;
    data.type = 2;
    data.clientVersion = clientVersion;
    data.capabilities = App.settings.capabilities;
    // don't pass tracking Id for upvotes so that it is generated by the server using a special format (with :vsid: in it)
    // Also that tracking Id won't be used now that we'll upload attachments to tracking Id of comment associated with vote instead of vote itself
    data.trackingId = null;

    dispatch({ type: Actions.VOTE_REQUEST, __rest: { item: item, operationKey } });

    platformActions.post(true, Apis.V2Verbatim, data).then((req) => {
        const voteTrackingId: string = (req as any).data.trackingId;
        console.log('voteFor.voteTrackingId: ' + voteTrackingId);

        dispatch({ type: Actions.VOTE_SUCCESS, __rest: { item: item, operationKey }, voteTrackingId: voteTrackingId, voteStartTimeMsec });
        if (voteTrackingId == null || voteTrackingId === "") {
            return;
        }

        if (!isVoteEnhanced) {
            return;
        }

        // process enhanced vote
        // tslint:disable-next-line:triple-equals
        if ((item != null) && (commentText !== "")) {
            // note: addComment will clear out the comment_text field, by design
            addComment({
                item,
                commentText,
                parentId: item.id,
                visibility: NodeVisibility.all,
                newNodeTrackingId: commentTrackingId,
            });
            console.log('voteFor.commentTrackingId: ' + commentTrackingId);

            // If in unlikely case addComment somehow fails then files will be uploaded but then cleaned up by temporary attachment cleaner
            App.startUploadingFiles(commentTrackingId, state);
        }
    }).catch((error) => {
        dispatch({ type: Actions.VOTE_FAILURE, __rest: { item: item, operationKey, error, errorMessage: stringFromErrorOrAny(error) } });

        // if already voted
        if (error?.response?.status === HttpResponse.Conflict) {
            setGlobalNotification({
                type: "information",
                canDismiss: true,
                message: "You have already voted for this feedback"
            } as NotificationInfo)
        }
        else {
            setGlobalNotification({
                type: "error",
                canDismiss: true,
                title: ResourceStrings.unexpectedError(),
                message: ResourceStrings.voteErrorOnSend()
            } as NotificationInfo)
        }
    });
};

export const reportProblem = (title: string, details: string, additionalTags: FeedbackTag[], allowedTemplateTagTypes: string[] = null): void => {
    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    const state: AppState = getState();

    const template = App.settings.template;

    // If we recorded for a VS instance different from the one that started reportProblem
    // update the tags before sending them
    if (state.settings_update != null) {
        for (let i = 0; i < template.tags.length; i++) {
            for (let j = 0; j < state.settings_update.tags.length; j++) {
                // eslint-disable-next-line security/detect-object-injection
                if (template.tags[i].type === state.settings_update.tags[j].type) {
                    // eslint-disable-next-line security/detect-object-injection
                    template.tags[i].value = state.settings_update.tags[j].value;
                }
            }
        }
    }

    const data = JSON.parse(JSON.stringify(template));
    tryReserveNewTrackingId(); // reserve new tracking ID to support reporting another problem (introduced with Nexus session support)
    data.submissionStartTimeMsec = Date.now();
    data.title = title;
    data.text = details;
    data.clientVersion = clientVersion;
    if (state.reportIncludeScreenshot) {
        data.tags.push({
            type: "user-resource",
            value: "screenshot",
        });
    }

    data.spaceId = state.reportSpaceId;
    // Backend has issues with routing the tickets for some non-VS spaces when source is not set to developer-community
    // So override source to developer-community for the case when user launches devcom2 from VS (which sets the source to 'send a smile') but submits report for other space
    if (data.spaceId !== SpaceId.VS && data.spaceId !== SpaceId.Internal) {
        data.source = RapSource.developerCommunity;
    }

    data.type = state.rapPostType === EntryValue.suggestion ? CommentType.Suggestion : CommentType.Issue;
    data.capabilities = App.settings.capabilities;

    // If user is connected to VS but reporting for other spaces then don’t include all the other VS specific tags which come with the template from native process
    // nikolami: 2025-02-03: commented since there's value in including all tags even when reporting for non-VS spaces. Also enabled SSMS to pass tags through
    // if (hasItems(allowedTemplateTagTypes)) {
    //     data.tags = data.tags.filter((tag: FeedbackTag) => allowedTemplateTagTypes.indexOf(tag.type) !== -1);
    // }

    // Add space specific tags
    if (hasItems(additionalTags)) {
        data.tags = [...data.tags, ...additionalTags];
    }

    dispatch({ type: Actions.RAP_SUBMIT_REQUEST });

    // check if sign-in is required, then send feedback if no signin required
    const fsid: string = App.settings.feedbackSessionId;
    const isNative: boolean = platformActions.getNativeActions() != null;
    platformActions.get(false,
        `${Apis.V2VerbatimCheckAuth}?fsid=${encodeURIComponent(fsid)}&isNative=${isNative}&source=devcom`
    ).then((result) => {
        console.log(result);

        // auth OK, send feedback
        platformActions.post(true, Apis.V2Verbatim, data).then((req) => {
            const trackingId: string = (req as any).data.trackingId;
            console.log('reportProblem.trackingId: ' + trackingId);
            if (!isNullOrEmpty(trackingId)) {
                // start uploading files to the tracking ID
                App.startUploadingFiles(trackingId, state);
            }

            dispatch({ type: Actions.RAP_SUBMIT_SUCCESS, req, __rest: { data: data } });
        }).catch((error) => {
            dispatch({ type: Actions.RAP_SUBMIT_FAILURE, error });
            setGlobalNotification({
                type: "error",
                canDismiss: true,
                title: ResourceStrings.unexpectedError(),
                message: stringFromErrorOrAny(error),
            } as NotificationInfo);
        });
    }).catch(error => {
        let errorMessage: string = stringFromErrorOrAny(error);

        const isUnauthorized: boolean =
            error?.response?.status === HttpResponse.Unauthorized ||  // for web
            (error?.text && error?.text?.indexOf("401 Unauthorized") >= 0); // for native

        if (isUnauthorized) {
            errorMessage = "Sign-in required. Please sign-in again and retry.";
            if (isNative) {
                dispatch({ type: Actions.RAP_SUBMIT_FAILURE, error });
                setGlobalNotification({
                    type: "error",
                    canDismiss: true,
                    title: ResourceStrings.unexpectedError(),
                    message: errorMessage,
                    onDismiss: () => {
                        // try signing in again, and report problem again on success
                        App.requestSignOutAndIn(() => reportProblem(title, details, additionalTags, allowedTemplateTagTypes));
                    },
                } as NotificationInfo);
                return;
            }
        }

        dispatch({ type: Actions.RAP_SUBMIT_FAILURE, error });
        setGlobalNotification({
            type: "error",
            canDismiss: true,
            title: ResourceStrings.unexpectedError(),
            message: errorMessage,
        } as NotificationInfo);
    });
};

export const clearSubmittedPostRecently = (): void => {
    App.store.dispatch({
        type: Actions.RAP_CLEAR_SUBMITTED_POST_RECENTLY
    });
};

export const clearReportProblem = (): void => {
    App.store.dispatch({
        type: Actions.RAP_CLEAR_FORM
    });
};

/** call local server instead of sendvsfeedback backend */
function getLocalCall(authRequired: boolean, api: LocalServerApis, actionName: string, actionData: any = {}): Promise<void> {
    const dispatch = App.store.dispatch;

    dispatch({ type: actionName + "_REQUEST", ...actionData });
    const localServer: LocalServerActions | null = platformActions.getLocalServerActions();
    if (!localServer) {
        const errorMessage = "ERR_UNSUPPORTED: If you're connected to VS, access this page from web only";
        dispatch({ type: actionName + "_FAILURE", ...actionData, errorMessage });
        return Promise.reject(errorMessage);
    }

    localServer.get(authRequired, `api${api}`).then((req) => {
        dispatch({ type: actionName + "_SUCCESS", req, ...actionData });
    }).catch((reason: any) => {
        dispatch({ type: actionName + "_FAILURE", ...actionData, errorMessage: reason });
    });
}

/** call local server instead of sendvsfeedback backend */
function postLocalCall(api: LocalServerApis, actionName: string, postData: any, actionData: any = {}): Promise<void> {
    const dispatch = App.store.dispatch;

    dispatch({ type: actionName + "_REQUEST", ...actionData });
    const localServer: LocalServerActions | null = platformActions.getLocalServerActions();
    if (!localServer) {
        const errorMessage = "ERR_UNSUPPORTED: If you're connected to VS, access this page from web only";
        dispatch({ type: actionName + "_FAILURE", ...actionData, errorMessage });
        return Promise.reject(errorMessage);
    }

    return localServer.post(true, `api${api}`, postData).then((req) => {
        dispatch({ type: actionName + "_SUCCESS", req, ...actionData });
    }).catch((reason: any) => {
        dispatch({ type: actionName + "_FAILURE", ...actionData, errorMessage: reason });
    });
}

function getCall(authRequired: boolean, api: string, actionName: string, actionData: any = {}): Promise<void> {
    const dispatch = App.store.dispatch;

    dispatch({ type: actionName + "_REQUEST", ...actionData });
    return platformActions.get(authRequired, api).then((req) => {
        dispatch({ type: actionName + "_SUCCESS", req, ...actionData });
    }).catch((reason: any) => {
        dispatch({ type: actionName + "_FAILURE", ...actionData, errorMessage: reason });
    });
}

function postCall(authRequired: boolean, api: string, actionName: string, postData: any, actionData: any = {}): Promise<void> {
    const dispatch = App.store.dispatch;

    dispatch({ type: actionName + "_REQUEST", ...actionData });
    return platformActions.post(authRequired, api, postData).then((req) => {
        dispatch({ type: actionName + "_SUCCESS", req, ...actionData });
    }).catch((reason: any) => {
        dispatch({ type: actionName + "_FAILURE", ...actionData, errorMessage: reason });
    });
}

export const setRoutingMessage = (productBranch: string, productBuild: string): void => {
    // Call the isPublicBuild API to determine if the Microsoft internal user's build is private or public in order to show the proper message for them when submitting
    const dispatch = App.store.dispatch;
    const getState: () => AppState = () => App.store.getState().appState;

    dispatch({ type: Actions.RAP_DETERMINE_IS_PUBLIC_BUILD_REQUEST });
    const partialUrl = `${Apis.DetailsV2IsPublicBuild}?branch=${encodeURIComponent(productBranch || '')}&productBuild=${encodeURIComponent(productBuild || '')}`;
    platformActions.get(true, partialUrl).then((req) => {
        const state: AppState = getState();
        dispatch({ type: Actions.RAP_DETERMINE_IS_PUBLIC_BUILD_SUCCESS, req });

        // If our last search was for a private build and we just signed in,
        // then we search again since we may have permission now.
        const searchInfo: SearchInfo = App.GetActiveSearchInfo(state);
        if (searchInfo && searchInfo.results.isPrivateBuild) {
            searchFor(searchInfo.searchtext, App.GetActiveSearchInfoTarget(state));
        }
    }).catch((reason: any) => {
        dispatch({ type: Actions.RAP_DETERMINE_IS_PUBLIC_BUILD_FAILURE });
    });
};

export const getApprovedFileExtensions = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({
        type: Actions.GET_APPROVED_FILE_EXTENSIONS,
        promise: platformActions.get(false, Apis.DiagnosticsApprovedFileExtensions),
    });
};

export const loadUserProperties = (): void => {
    const template: any = JSON.parse(JSON.stringify(App.settings.template));
    // don't sent dotnet and watson data on user properties - it's not needed and can be big
    template.dotNetEntries = [];
    template.watsonEntries = [];
    postCall(true, Apis.UserPropertiesGetUserProperties, Actions.LOAD_USER_PROPERTIES, {
        searchFor: template,
        capabilities: App.settings.capabilities,
    });
};

export const loadUserProfile = (vsidOrPrincipalId: string): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.USER_PROFILE_LOAD_REQUEST });

    platformActions
        .get(false, `${Apis.UserPropertiesProfile}?vsidOrPrincipalId=${vsidOrPrincipalId}`)
        .then((req) => {
            dispatch({
                type: Actions.USER_PROFILE_LOAD_SUCCESS,
                req
            });
        })
        .catch((reason: any) => {
            dispatch({
                type: Actions.USER_PROFILE_LOAD_FAILURE,
                errorMessage: stringFromErrorOrAny(reason),
            });
        });
};

export const getUserMetadata = (): void => {
    const dispatch = App.store.dispatch;
    dispatch({ type: requestAction(Actions.GET_USER_METADATA) });

    platformActions.get(true, Apis.UserPropertiesMetadata)
        .then((req) => {
            dispatch({ type: successAction(Actions.GET_USER_METADATA), req });
        }).catch((reason: any) => {
            dispatch({
                type: failureAction(Actions.GET_USER_METADATA),
                errorMessage: reason.message || reason || "err_unknown",
            });
        });
};

export const loadActivity = (pageNumberFromOne: number = 1): void => {
    Actions.lastGetActivityId++;
    const currGetActivityId: number = Actions.lastGetActivityId;
    postCall(true, Apis.UserPropertiesGetActivity, Actions.LOAD_ACTIVITY,
        {
            page: pageNumberFromOne,
            pageSize: ACTIVITY_PAGE_SIZE,
            clientVersion: clientVersion,
            capabilities: App.settings.capabilities,
        },
        { currGetActivityId, pageNumberFromOne });
};

export const loadAnnouncements = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.LOAD_ANNOUNCEMENTS_REQUEST });

    platformActions.get(false, `${Apis.AnnouncementAnnouncements}?source=devcom2`).then((req) => {
        dispatch({ type: Actions.LOAD_ANNOUNCEMENTS_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.LOAD_ANNOUNCEMENTS_FAILURE,
            errorMessage: reason.message || reason || "err_unknown",
        });
    });
};

export const loadIncidents = (): void => {
    const dispatch = App.store.dispatch;
    dispatch({ type: Actions.LOAD_INCIDENTS_REQUEST });

    platformActions.get(false, `${Apis.AnnouncementIncidents}?source=devcom2`).then((req) => {
        dispatch({ type: Actions.LOAD_INCIDENTS_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.LOAD_INCIDENTS_FAILURE,
            errorMessage: reason.message || reason || "err_unknown",
        });
    });
};

export const loadOutage = (): void => {
    const dispatch = App.store.dispatch;
    dispatch({ type: Actions.LOAD_VSSTATUS_OUTAGE_REQUEST });

    platformActions.get(false, Apis.AnnouncementStatus).then((req) => {
        dispatch({ type: Actions.LOAD_VSSTATUS_OUTAGE_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.LOAD_VSSTATUS_OUTAGE_FAILURE,
            errorMessage: reason.message || reason || "err_unknown",
        });
    });
};

export const loadCustomOutage = (): void => {
    const dispatch = App.store.dispatch;
    dispatch({ type: Actions.LOAD_CUSTOM_OUTAGE_REQUEST });

    platformActions.get(false, Apis.AnnouncementCustomOutage).then((req) => {
        dispatch({ type: Actions.LOAD_CUSTOM_OUTAGE_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.LOAD_CUSTOM_OUTAGE_FAILURE,
            errorMessage: reason.message || reason || "err_unknown",
        });
    });
};

export const loadExtraAdminInfo = (): void => {
    getLocalCall(true, LocalServerApis.ConfigRouting, Actions.GET_ROUTING_RULES);
    getLocalCall(true, LocalServerApis.ConfigAuditLogs, Actions.GET_AUDIT_LOGS);
    getLocalCall(true, LocalServerApis.ConfigFeatureFlags, Actions.GET_FEATURE_FLAGS);
    getLocalCall(true, LocalServerApis.ConfigModAdminUsers, Actions.GET_MOD_ADMIN_USERS);
    getLocalCall(true, LocalServerApis.ConfigPipelineStatus, Actions.GET_PIPELINE_STATUS);
    getLocalCall(true, LocalServerApis.ConfigGdprStatus, Actions.GET_GDPR_STATUS);
};


export const loadSiteAndSpaceConfig = (): void => {
    getLocalCall(true, LocalServerApis.Config, Actions.GET_SITE_SPACE_CONFIG);
};

export const loadFeatureFlagSettings = (): void => {
    getLocalCall(true, LocalServerApis.ConfigFeatureFlags, Actions.GET_FEATURE_FLAGS);
};

export const createOrEditSpaceConfig = (json: string): void => {
    postLocalCall(LocalServerApis.ConfigSpace, Actions.UPDATE_SPACE_CONFIG, json)
        .then(loadSiteAndSpaceConfig);
};

export const createOrEditWebsiteConfig = (json: string): void => {
    postLocalCall(LocalServerApis.ConfigWebsite, Actions.UPDATE_WEBSITE_CONFIG, json)
        .then(loadSiteAndSpaceConfig);
};

export const createOrEditFeatureFlag = (json: string): void => {
    postLocalCall(LocalServerApis.ConfigFeatureFlags, Actions.UPDATE_FEATURE_FLAG, json)
        .then(loadFeatureFlagSettings);
};

export const setAnnouncementType = (selectedAnnouncement: string): void => {
    App.store.dispatch({
        type: Actions.SET_SELECTED_ANNOUNCEMENT,
        selectedAnnouncement: selectedAnnouncement,
    });
};

export const viewAttachments = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.VIEW_ATTACHMENTS_REQUEST });

    platformActions.get(true, Apis.DiagnosticsAttachmentsPerUser)
        .then((req) => {
            dispatch({ type: Actions.VIEW_ATTACHMENTS_SUCCESS, req });
        }).catch((reason: any) => {
            dispatch({
                type: Actions.VIEW_ATTACHMENTS_FAILURE,
                errorMessage: stringFromErrorOrAny(reason),
            });
        });
};

export const downloadAttachment = (info: AttachmentPostInfo): void => {
    App.openSafe(`${getDevComUrl()}/api/file/${info.transferId}`);
};

export const deleteAttachment = (info: AttachmentPostInfo): void => {
    postCall(true, Apis.DiagnosticsDeleteAttachment, Actions.DELETE_ATTACHMENT, { transferId: info.transferId }, { deletedTransferId: info.transferId });
};

export const createArchive = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.CREATE_ARCHIVE_REQUEST });

    const partialUrl = `${Apis.ExportRestrictExport}?vsid=${App.getVsid(App.settings.template)}`;
    const data = {};

    platformActions.post(true, `${partialUrl}`, data).then((req) => {
        dispatch({ type: Actions.CREATE_ARCHIVE_SUCCESS, req });  // response body is empty
        getArchiveStatus();
    }).catch((reason: any) => {
        dispatch({
            type: Actions.CREATE_ARCHIVE_FAILURE,
            errorMessage: stringFromErrorOrAny(reason),
        });
    });
};

export const getArchiveStatus = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.ARCHIVE_STATUS_REQUEST });

    const partialUrl = `${Apis.ExportRestrictStatus}?vsid=${App.getVsid(App.settings.template)}`;

    platformActions.get(true, `${partialUrl}`).then((req) => {
        dispatch({ type: Actions.ARCHIVE_STATUS_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.ARCHIVE_STATUS_FAILURE,
            errorMessage: stringFromErrorOrAny(reason),
        });
    });
}

export const getUserServerSettings = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.USER_SERVER_SETTINGS_REQUEST });

    platformActions.get(true, Apis.UserPropertiesSettings).then((req) => {
        dispatch({ type: Actions.USER_SERVER_SETTINGS_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.USER_SERVER_SETTINGS_FAILURE,
            errorMessage: stringFromErrorOrAny(reason),
        });
    });
}

export const setUserServerSettingsLocally = (newSettings: UserServerSettings): void => {
    App.store.dispatch({
        type: Actions.SET_USER_SERVER_SETTINGS_LOCALLY,
        newSettings: newSettings,
    });
}

export const postUserServerSettings = (newSettings: UserServerSettings): void => {
    postCall(true, Apis.UserPropertiesSettings, Actions.SET_USER_SERVER_SETTINGS, newSettings);
}

export const downloadArchiveZip = (zipFileName: string): void => {
    App.openSafe(`${getDevComUrl()}/api/archive/${zipFileName}`);
}

export const deleteAccount = (): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.DELETE_ACCOUNT_REQUEST });

    const partialUrl = `${Apis.ExportRestrictDelete}?vsid=${App.getVsid(App.settings.template)}`;
    const data = {};

    platformActions.post(true, `${partialUrl}`, data).then((req) => {
        dispatch({ type: Actions.DELETE_ACCOUNT_SUCCESS, req });
        getArchiveStatus();
    }).catch((reason: any) => {
        dispatch({
            type: Actions.DELETE_ACCOUNT_FAILURE,
            errorMessage: stringFromErrorOrAny(reason),
        });
    });
}

export const followTicket = (answerHubId: number, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        NodeId: answerHubId
    }

    dispatch({
        type: Actions.FOLLOW_TICKET,
        data: { answerHubId, operationKey },
        promise: platformActions.post(true, Apis.UserPropertiesFollow, data),
    });
};

export const unFollowTicket = (answerHubId: number, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        NodeId: answerHubId
    }

    dispatch({
        type: Actions.UNFOLLOW_TICKET,
        data: { answerHubId, operationKey },
        promise: platformActions.post(true, Apis.UserPropertiesUnfollow, data),
    });
};

export const flagForModeration = (nodeId: number, answerHubId: number, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        NodeId: nodeId
    }

    dispatch({
        type: Actions.FLAG_FOR_MODERATION,
        data: { answerHubId, operationKey },
        promise: platformActions.post(true, Apis.NodeFlag, data),
    });
};

export const convertNode = (ticketId: number, operationKey: string, nodeId: number, newNodeType: string, newParentId: number): void => {
    const dispatch = App.store.dispatch;

    const apiData = {
        NodeId: nodeId,
        NewNodeType: newNodeType,
        NewParentId: newParentId
    };

    dispatch({ type: requestAction(Actions.CONVERT_NODE), ticketId, operationKey });

    platformActions.post(true, Apis.NodeConvert, apiData).then((req) => {
        dispatch({ type: successAction(Actions.CONVERT_NODE), req, ticketId, operationKey });
    }).catch((reason: any) => {
        dispatch({ type: failureAction(Actions.CONVERT_NODE), reason, ticketId, operationKey });
        console.log(reason);
    });
};

export const updateTicket = (ticketId: number, operationKey: string, title?: string, description?: string, addedTags?: string[], removedTags?: string[]): void => {
    const dispatch = App.store.dispatch;

    const apiData: any = {
        postId: ticketId,
    };
    if (title) {
        apiData.title = title;
    }
    if (description) {
        apiData.description = description;
    }
    if (addedTags) {
        apiData.addedTags = addedTags;
    }
    if (removedTags) {
        apiData.removedTags = removedTags;
    }

    dispatch({ type: requestAction(Actions.UPDATE_TICKET), ticketId, operationKey });

    platformActions.post(true, Apis.NodeUpdate, apiData).then((req) => {
        dispatch({ type: successAction(Actions.UPDATE_TICKET), req, ticketId, operationKey });
    }).catch((reason: any) => {
        dispatch({ type: failureAction(Actions.UPDATE_TICKET), reason, ticketId, operationKey });
        console.log(reason);
    });
};

// For solution and comments
export const voteOnNode = (rootPostId: RootPostId, postId: PostId, operationKey: string, voteType: ReactionType, isCancelled: boolean): void => {
    const apiData: any = {
        NodeId: postId,
        ReactionType: isCancelled ? VoteType.cancelVote : voteType === ReactionType.upvote ? VoteType.voteUp : VoteType.voteDown,
    };

    postCall(true, Apis.NodeVote, Actions.VOTE_ON_NODE, apiData, {rootPostId, postId, voteType, operationKey, isCancelled })
};

export const reactivate = (ticketId: number, operationKey: string, nodeIdToReactivate: number, reactionType: ReactionType, visibility: NodeVisibility, trackingId: string, commentText: string = null): void => {
    const dispatch = App.store.dispatch;

    const apiData: any = {
        NodeId: nodeIdToReactivate,
        ReactionType: reactionType,
        Visibility: visibility,
        CommentText: commentText,
        TrackingId: trackingId
    };

    dispatch({ type: requestAction(Actions.REACTIVATE_NODE), ticketId, operationKey });

    platformActions.post(true, Apis.NodeReactivate, apiData).then((req) => {
        dispatch({ type: successAction(Actions.REACTIVATE_NODE), req, ticketId, operationKey });
    }).catch((reason: any) => {
        dispatch({ type: failureAction(Actions.REACTIVATE_NODE), error: reason, ticketId, operationKey, errorMessage: stringFromErrorOrAny(reason) });
        console.log(reason);
    });
};

export const deleteNode = (nodeId: number, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        NodeId: nodeId
    }

    dispatch({
        type: Actions.DELETE_NODE,
        data: { operationKey },
        promise: platformActions.post(true, Apis.NodeDelete, data),
    });
};

export const setReleaseActionInfo = (channelManifestId: string, product: string): void => {
    const dispatch = App.store.dispatch;

    dispatch({ type: Actions.RELEASE_ACTION_INFO_REQUEST });

    platformActions.get(false,
        `${Apis.DetailsV2GetReleaseAction}?channelManifestId=${channelManifestId}&product=${product}`
    ).then((req) => {
        dispatch({ type: Actions.RELEASE_ACTION_INFO_SUCCESS, req });
    }).catch((reason: any) => {
        dispatch({
            type: Actions.RELEASE_ACTION_INFO_FAILURE,
            errorMessage: stringFromErrorOrAny(reason),
        });
    });
};

export const loadFlaggedPosts = (moderationListFilter: ModerationListFilter, pageNumber: number, pageSize: number): void => {
    const dispatch = App.store.dispatch;

    dispatch({
        type: Actions.LOAD_FLAGGED_POSTS,
        pageNumber,
        pageSize,
        promise: platformActions.get(false, `${Apis.ModerationGetFlaggedPosts}?moderationListFilter=${moderationListFilter}&pageNumber=${pageNumber}&pageSize=${pageSize}`),
    });
};

export const loadRecentlyCreatedPosts = (pageNumber: number, pageSize: number): void => {
    const dispatch = App.store.dispatch;

    dispatch({
        type: Actions.LOAD_RECENTLY_CREATED_POSTS,
        pageNumber,
        pageSize,
        promise: platformActions.get(false, `${Apis.ModerationRecentlyCreatedPosts}?pageNumber=${pageNumber}&pageSize=${pageSize}`),
    });
};

export const loadAutoRejectedPosts = (pageNumber: number, pageSize: number): void => {
    const dispatch = App.store.dispatch;

    dispatch({
        type: Actions.LOAD_AUTO_REJECTED_POSTS,
        pageNumber,
        pageSize,
        promise: platformActions.get(false, `${Apis.ModerationAutoRejectedPosts}?pageNumber=${pageNumber}&pageSize=${pageSize}`),
    });
};

export const moderationPublishPost = (postId: number, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        PostId: postId
    }

    dispatch({
        type: Actions.MODERATE_PUBLISH_POST,
        data: { postId, operationKey },
        promise: platformActions.post(true, Apis.ModerationPublishPost, data),
    });
};

export const moderationRejectPost = (postId: number, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        PostId: postId
    }

    dispatch({
        type: Actions.MODERATE_REJECT_POST,
        data: { postId, operationKey },
        promise: platformActions.post(true, Apis.ModerationRejectPost, data),
    });
};

export const updateUserSuspensionState = (userId: number, actionType: SuspensionActionType, operationKey: string): void => {
    const dispatch = App.store.dispatch;

    const data = {
        UserId: userId,
        ActionType: actionType,
    }

    dispatch({
        type: Actions.UPDATE_USER_SUSPENSION_STATE,
        data: { userId, actionType, operationKey },
        promise: platformActions.post(true, Apis.UserPropertiesUpdateSuspensionState, data),
    });
};

export const searchUsersForAdminView = (searchText: string, pageNumber: number, pageSize: number): void => {
    const dispatch = App.store.dispatch;

    dispatch({
        type: Actions.SEARCH_USERS_FOR_ADMIN_VIEW,
        searchText,
        pageNumber,
        pageSize,
        promise: platformActions.get(false, `${Apis.UserPropertiesSearchUsers}?searchText=${encodeURIComponent(searchText ?? '')}&pageNumber=${pageNumber}&pageSize=${pageSize}`),
    });
};

